import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";

import {
  CONTENT_CATEGORIES,
  CONTENT_STATUS,
  fetchTipById,
  updateTip,
} from "../../../services/contentService";

function StatusPill({ status }) {
  const map = {
    [CONTENT_STATUS.PUBLISHED]: "is-published",
    [CONTENT_STATUS.REVIEW]: "is-review",
    [CONTENT_STATUS.DRAFT]: "is-draft",
  };

  return (
    <span className={`ac-content-statuspill ${map[status] || ""}`}>
      {status}
    </span>
  );
}

export default function TipDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    category: "Stress",
    status: CONTENT_STATUS.DRAFT,
    excerpt: "",
    body: "",
  });

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const data = await fetchTipById(id);

        if (!mounted) return;
        setTip(data);

        if (data) {
          setForm({
            title: data.title || "",
            category: data.category || "Stress",
            status: data.status || CONTENT_STATUS.DRAFT,
            excerpt: data.excerpt || "",
            body: data.body || "",
          });
        }
      } catch (error) {
        console.error("Tipp konnte nicht geladen werden:", error);
        if (!mounted) return;
        setTip(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [id]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  async function handleSave() {
    try {
      setSaving(true);
      await updateTip(id, form);
      navigate("/content/tips");
    } catch (error) {
      console.error("Tipp konnte nicht gespeichert werden:", error);
      alert("Änderungen konnten nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <DashboardLayout title="Inhalte · Tipp Details">
        <div className="ac-soft-panel ac-content-block">
          <div className="text-lg font-extrabold text-ac-text">Tipp wird geladen...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!tip) {
    return (
      <DashboardLayout title="Inhalte · Tipp">
        <div className="ac-soft-panel ac-content-block">
          <div className="text-lg font-extrabold text-ac-text">Tipp nicht gefunden</div>
          <button
            onClick={() => navigate("/content/tips")}
            className="mt-4 ac-ghostbtn"
          >
            Zurück zur Liste
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Inhalte · Tipp Details">
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xl font-extrabold text-ac-text">Tipp Details</div>

          <button onClick={() => navigate("/content/tips")} className="ac-ghostbtn">
            ← Tipps
          </button>
        </div>

        <section className="ac-soft-panel ac-content-hero">
          <div className="ac-content-hero-grid">
            <div>
              <h2 className="ac-content-hero-title">
                {form.title || "Tipp Details"}
              </h2>
              <p className="ac-content-hero-text">
                Bearbeite Titel, Kurzbeschreibung, Inhalt und Status direkt hier.
              </p>
            </div>

            <div className="ac-content-hero-side">
              <div className="ac-content-acebox">
                <AceAvatar variant="ruhig2" size={56} />
                <div className="ac-content-acebubble">
                  Ace sagt: Kurz, klar und realistisch — so wird ein Tipp eher genutzt.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-soft-panel ac-content-block">
          <div className="ac-content-detail-meta">
            <div className="ac-content-detail-meta-card">
              <div className="ac-content-label">Status</div>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <StatusPill status={form.status} />
                <select
                  value={form.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="ac-select"
                >
                  <option value={CONTENT_STATUS.DRAFT}>{CONTENT_STATUS.DRAFT}</option>
                  <option value={CONTENT_STATUS.REVIEW}>{CONTENT_STATUS.REVIEW}</option>
                  <option value={CONTENT_STATUS.PUBLISHED}>{CONTENT_STATUS.PUBLISHED}</option>
                </select>
              </div>
            </div>

            <div className="ac-content-detail-meta-card ac-content-detail-meta-wide">
              <div className="ac-content-detail-grid">
                <div>
                  <label className="ac-content-label">Kategorie</label>
                  <select
                    value={form.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                    className="ac-select mt-2 w-full"
                  >
                    {CONTENT_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ac-content-label">Zuletzt bearbeitet von</label>
                  <div className="ac-content-staticbox mt-2">{tip.author}</div>
                </div>

                <div>
                  <label className="ac-content-label">Zuletzt aktualisiert</label>
                  <div className="ac-content-staticbox mt-2">{tip.updatedAtLabel}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="ac-content-field mt-5">
            <label className="ac-content-label">Titel</label>
            <input
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className="ac-content-input"
            />
          </div>

          <div className="ac-content-field mt-5">
            <label className="ac-content-label">Kurzbeschreibung</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => handleChange("excerpt", e.target.value)}
              rows={3}
              className="ac-content-textarea"
            />
          </div>

          <div className="ac-content-field mt-5">
            <label className="ac-content-label">Inhalt</label>
            <textarea
              value={form.body}
              onChange={(e) => handleChange("body", e.target.value)}
              rows={10}
              className="ac-content-textarea"
            />
          </div>

          <div className="ac-content-actions-row">
            <button onClick={() => navigate("/content/tips")} className="ac-ghostbtn ac-content-smallbtn">
              Zur Tipps Liste
            </button>

            <button
              onClick={handleSave}
              className="ac-primarybtn ac-content-smallbtn"
              disabled={saving}
            >
              {saving ? "Speichert..." : "Änderungen speichern"}
            </button>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}